import React, { useEffect } from 'react';
import { Route, Routes, Outlet } from 'react-router-dom';

import CacheBuster from 'react-cache-buster';
import { ThemeProvider } from 'styled-components';

import { CriticalErrorBoundary } from './CriticalErrorBoundary';

import {
  ACCOUNT,
  ADD_FID_CARD,
  ADD_FID_SCAN,
  AMOUNT_CHANGE_JACKPOT,
  CART,
  CHECK,
  CONFIG,
  CONVERSION,
  CREATE_FID_CARD_CASHBACK,
  DEEPLINK_5E,
  EDENRED,
  EDENRED_CONFIG,
  FIDCOUPON,
  FIDELITY_FORM,
  HOME,
  INVITATION,
  LOGIN,
  NUMBER_SCREEN,
  ORDER_SUCCESS,
  PHONE_PASSWORD_LOGIN,
  PIKKO_JACKPOT,
  POLICY,
  POPUP_PAYMENT,
  PRODUCT,
  PROMO,
  RECEIPT,
  RECOMPENSES,
  REGISTER,
  RESETPASSWORD,
  REWARD,
  SCAN,
  SCAN_CHECK,
  SET_ACCOUNT,
  STORE_LIST,
  STORE_LOCATION,
  TICKETS,
  UPDATE_USER,
  USER_FID_CARD,
  USER_FID_CARD_DETAIL,
} from './constants/routes';

import UserFeedbackDrawer from './components/feedback/UserFeedbackDrawer';
import { useFeedbackDrawer } from './hooks/useFeedbackDrawer';
import { useShadowDOMStyling } from './hooks/useShadowDOMStyling';

import Scan from './components/Scan';
import PageLoader from './components/loader/PageLoader';
import CartScreen from './pages/CartScreen';
import ConfigScreen from './pages/Config/ConfigPage';
import RedirectPage from './pages/RedirectPage';
import ScanCheck from './pages/ScanCheck';
import StoreListScreen from './pages/StoreListScreen';
import WelcomeScreen from './pages/WelcomeScreen';
import AccountScreen from './pages/account/AccountScreen';
import NumberScreen from './pages/account/NumberScreen';
import UserFidelityCard from './pages/account/fid/UserFidelityCard';
import UserFidelityCardDetail from './pages/account/fid/UserFidelityCardDetail';
import UserAddFidCardScreen from './pages/account/fid/add-card/UserAddFidCardScreen';
import UserAddFidScanScreen from './pages/account/fid/add-card/UserAddFidScanScreen';
import SetAccountScreen from './pages/account/setAccout/SetAccountScreen';
import LoginScreen from './pages/auth/LoginScreen';
import RegisterScreen from './pages/auth/register/RegisterScreen';

import EdenredScreen from './pages/EdenredScreen';
import OrderCheck from './pages/OrderCheck';
import OrderSuccess from './pages/OrderSuccess';
import ProductScreen from './pages/ProductScreen';

import InvitationScreen from './pages/Invitation';
import PopupPayment from './payments/PopupPayment';

import PikkoJackpotScreen from './pages/PikkoJackpotScreen';
import Recompenses from './pages/account/Recompense';
import TicketScreen from './pages/account/TicketScreen';
import UpdateUserScreen from './pages/account/UpdateUserScreen';
import ResetPassword from './pages/auth/reset-password/ResetPassword';
import RewardScreen from './pages/gift/RewardScreen';
import ChangeAmountJackpotScreen from './pages/jackpot/ChangeAmountJackpotScreen';
import PromoConversionScreen from './pages/promo/PromoConversionScreen';
import PromoScreen from './pages/promo/PromoScreen';

import { faArrowDown, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PullToRefresh from 'pulltorefreshjs';
import ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';
import { isAppPWA, getDevicePlatform } from './utils/helper-functions';

import FidCouponScreen from './pages/FidCouponScreen';
import ReceiptScreen from './pages/ReceiptScreen';

import { GeolocationVerifModal } from './components/geolocation/modal/GeolocationVerifModal';
import StoreLocationScreen from './pages/StoreLocationScreen';

import CrashReportEventListener from './components/crashReport/CrashReportEventListener';
import CrashReportModal from './components/crashReport/CrashReportModal';
import { CrashReportProvider } from './context/CrashReportProvider';

import { AppNotification } from './components/crashReport/appNotification';
import { NotificationProvider } from './context/notificationProvider';

import useAxiosInterceptors from './axios/useAxiosInterceptors';
import { NavigationTracker } from './components/NavigationTracker';
import GlobalNotificationPikkoToast from './components/toasts/GlobalNotificationPikkoToast';
import { PerformanceContextProvider } from './context/PerformanceContext';

import { ToastProvider } from './components/toasts/provider/providerPikkoToast';
import { ProtectedRoute } from './middleware/ProtectedRoute';
import DeeplinkScreen from './pages/DeeplinkScreen';
import FidelityForm from './pages/account/fid/fidelityForms/FidelityForm';
import { CreateFidCardCashback } from './pages/jackpot/CreateFidCardCashback';
import PrivacyPolicy from './pages/media/policy';
import { PhoneLoginRooter } from './pages/phoneLogin/PhoneLoginRooter';
import NotFoundScreen from './pages/NotFoundScreen';
function App() {
  useAxiosInterceptors();
  useEffect(() => {
    const { isAndroid, isIOS } = getDevicePlatform();

    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';

    // Load the correct manifest based on the detected OS
    if (isAndroid) {
      manifestLink.href = '/manifest-android.json';
    } else if (isIOS) {
      manifestLink.href = '/manifest-ios.json';
    } else {
      manifestLink.href = '/manifest-default.json';
    }
    document.head.appendChild(manifestLink);

    return () => {
      document.head.removeChild(manifestLink);
    };
  }, []);

  useEffect(() => {
    if (document.querySelector('#pullToRefreshArea')) {
      PullToRefresh.init({
        mainElement: '#pullToRefreshArea',
        triggerElement: '#pullToRefreshArea',
        shouldPullToRefresh() {
          return isAppPWA();
        },
        onRefresh() {
          window.location.reload();
        },
        iconArrow: ReactDOMServer.renderToString(
          <FontAwesomeIcon icon={faArrowDown} size={'2x'} />,
        ),
        iconRefreshing: ReactDOMServer.renderToString(
          <FontAwesomeIcon icon={faSyncAlt} spin={true} size={'2x'} />,
        ),
        instructionsPullToRefresh: ' ',
        instructionsReleaseToRefresh: ' ',
        instructionsRefreshing: ' ',
      });
    }
  });

  window.scrollTo(0, 0);

  const actualTheme = useSelector((state) => state.theme.theme);

  // Sentry user feedBack config
  const configSelectorSentry = {
    shadowRootId: 'sentry-feedback',
    selector: '.widget__actor',
    maxRetries: 20,
    retryInterval: 150,
  };

  const routeStylesForSentry = {
    '/': {
      bottom: '70%',
      rigth: '0%',
    },
    '/home': {
      bottom: '60%',
      rigth: '0%',
    },
    '/scan': {
      bottom: '63%',
      rigth: '0%',
    },
    '/cart': {
      bottom: '89%',
      rigth: '0%',
    },
    '/receipt': {
      bottom: '53%',
      rigth: '0%',
    },
    '/account': {
      bottom: '85%',
      rigth: '0%',
    },
  };

  useShadowDOMStyling(configSelectorSentry, routeStylesForSentry);

  // Sentry userFeedBack Drawer
  const { isDrawerOpen, closeDrawer, sentryButtonRef } = useFeedbackDrawer();

  const CHECKS = {
    CART: { slice: 'cart', key: 'cartItems._id' },
    USER: { slice: 'user' },
    STORE: { slice: 'store', key: 'store.id' },
  };

  return (
    <>
      <ThemeProvider theme={actualTheme}>
        <ToastProvider>
          <PerformanceContextProvider>
            <CrashReportProvider>
              <CrashReportEventListener />
              <CrashReportModal />
              <NavigationTracker />
              <CriticalErrorBoundary>
                <NotificationProvider>
                  <UserFeedbackDrawer
                    isOpen={isDrawerOpen}
                    onClose={closeDrawer}
                    triggerRef={sentryButtonRef}
                  />

                  <AppNotification />
                  <GlobalNotificationPikkoToast />

                  <div id="pullToRefreshArea">
                    <CacheBuster
                      currentVersion={process.env.REACT_APP_VERSION}
                      isEnabled={true}
                    >
                      {/* <BrowserRouter> */}
                      <div className="home">
                        <main>
                          {/*<PullToRefreshComponent />*/}
                          <React.Suspense fallback={<PageLoader />}>
                            <Routes>
                              <Route element={<><GeolocationVerifModal /><Outlet /></>}>
                              <Route path="/" element={<RedirectPage />} />
                              <Route
                                path={POLICY}
                                element={<PrivacyPolicy />}
                              />
                              <Route
                                path={STORE_LIST}
                                element={<StoreListScreen />}
                              />
                              <Route
                                path={STORE_LOCATION}
                                element={<StoreLocationScreen />}
                              />
                              <Route path={HOME} element={<WelcomeScreen />} />

                              <Route path={CONFIG} element={<ConfigScreen />} />

                              <Route
                                path={INVITATION}
                                element={<InvitationScreen />}
                              />
                              <Route
                                path={PRODUCT}
                                element={<ProductScreen />}
                              />
                              <Route
                                path={EDENRED}
                                element={<EdenredScreen />}
                              />
                              <Route
                                path={EDENRED_CONFIG}
                                element={<EdenredScreen />}
                              />
                              <Route path={CHECK} element={<OrderCheck />} />
                              <Route
                                path={ORDER_SUCCESS}
                                element={<OrderSuccess />}
                              />
                              <Route
                                path={SCAN}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART]}
                                    redirectTo={HOME}
                                  >
                                    <Scan />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={CART}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART]}
                                    redirectTo={HOME}
                                  >
                                    <CartScreen />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={SCAN_CHECK}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART]}
                                    redirectTo={HOME}
                                  >
                                    <ScanCheck />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={PIKKO_JACKPOT}
                                element={<PikkoJackpotScreen />}
                              />
                              <Route
                                path={RECEIPT}
                                element={<ReceiptScreen />}
                              />
                              <Route path={LOGIN} element={<LoginScreen />} />
                              <Route
                                path={REGISTER}
                                element={<RegisterScreen />}
                              />
                              <Route
                                path={ACCOUNT}
                                element={<AccountScreen />}
                              />
                              <Route
                                path={SET_ACCOUNT}
                                element={<SetAccountScreen />}
                              />
                              <Route
                                path={RESETPASSWORD}
                                element={<ResetPassword />}
                              />
                              <Route
                                path={TICKETS}
                                element={<TicketScreen />}
                              />
                              <Route
                                path={RECOMPENSES}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART, CHECKS.USER]}
                                    redirectTo={HOME}
                                  >
                                    <Recompenses />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={POPUP_PAYMENT}
                                element={<PopupPayment />}
                              />
                              <Route path={REWARD} element={<RewardScreen />} />
                              <Route
                                path={PROMO}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART]}
                                    redirectTo={HOME}
                                  >
                                    <PromoScreen />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={FIDCOUPON}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART]}
                                    redirectTo={HOME}
                                  >
                                    <FidCouponScreen />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={CONVERSION}
                                element={<PromoConversionScreen />}
                              />
                              <Route
                                path={ADD_FID_CARD}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.USER]}
                                    locationChecks={['companyId']}
                                    redirectTo={NUMBER_SCREEN}
                                  >
                                    <UserAddFidCardScreen />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={ADD_FID_SCAN}
                                element={<UserAddFidScanScreen />}
                              />
                              <Route
                                path={USER_FID_CARD}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.USER]}
                                    redirectTo={NUMBER_SCREEN}
                                  >
                                    <UserFidelityCard />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={USER_FID_CARD_DETAIL}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.USER]}
                                    redirectTo={NUMBER_SCREEN}
                                  >
                                    <UserFidelityCardDetail />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={NUMBER_SCREEN}
                                element={
                                  <NumberScreen allowOtherLoginMethod={true} />
                                }
                              />
                              <Route
                                path={`${PHONE_PASSWORD_LOGIN}/*`}
                                element={<PhoneLoginRooter />}
                              />
                              <Route
                                path={UPDATE_USER}
                                element={<UpdateUserScreen />}
                              />
                              <Route
                                path={AMOUNT_CHANGE_JACKPOT}
                                element={<ChangeAmountJackpotScreen />}
                              />
                              <Route
                                path={CREATE_FID_CARD_CASHBACK}
                                element={
                                  <ProtectedRoute
                                    checks={[CHECKS.CART]}
                                    redirectTo={HOME}
                                  >
                                    <CreateFidCardCashback />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path={DEEPLINK_5E}
                                element={<DeeplinkScreen />}
                              />
                              {/* <Route path="/Recompense" element = {<Recompense/>} /> */}
                              <Route path={FIDELITY_FORM} element={
                                <ProtectedRoute>
                                  <FidelityForm />
                                </ProtectedRoute>
                              } />
                              </Route>
                              <Route path="*" element={<NotFoundScreen />} />
                            </Routes>
                          </React.Suspense>
                        </main>
                      </div>
                      {/* </BrowserRouter> */}
                    </CacheBuster>
                  </div>
                </NotificationProvider>
              </CriticalErrorBoundary>
            </CrashReportProvider>
          </PerformanceContextProvider>
        </ToastProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
